import { DAY_MS } from "./freshness.js";

// The calendar-week boundary for weekly targets (PG-2, 2026-07-13). Weekly
// targets are a WEEKLY goal, so the "this week" the meter/coverage scores must
// reset on a fixed weekday, not slide as a trailing 7-day window ending "now"
// (which made the actual silently drop as sessions aged out mid-week — the
// "targets don't move as I'd expect" report). The reset day is the athlete's
// choice (Profile.weekStartDay), defaulting to Monday (ISO / EU).

// 0 = Sunday .. 6 = Saturday, matching JS Date.getDay(). Default Monday: the
// user is in the EU and the app already numbers weeks ISO (Monday-anchored).
export const DEFAULT_WEEK_START_DAY = 1;

/**
 * Epoch-ms of the most recent `weekStartDay` at 00:00 LOCAL time, at or before
 * `nowMs` — i.e. the start of the calendar week that contains `nowMs`.
 *
 * Local (not UTC) on purpose: a training week is anchored to the athlete's wall
 * clock. The returned value is still an absolute instant, so comparing a
 * session's absolute `startedAtMs` against it is correct regardless of zone.
 *
 * DST-correct: it walks back whole CALENDAR days via `setDate` (not a fixed
 * `days * 86_400_000`), so a week that contains a clock change still lands on
 * local midnight of the start day, not an hour off.
 */
export const weekWindowStart = (
  nowMs: number,
  weekStartDay: number = DEFAULT_WEEK_START_DAY,
): number => {
  const d = new Date(nowMs);
  // Days elapsed since the most recent start-of-week (0..6).
  const back = (d.getDay() - weekStartDay + 7) % 7;
  d.setHours(0, 0, 0, 0); // local midnight of today
  d.setDate(d.getDate() - back); // walk back to the week's start day
  return d.getTime();
};

/**
 * Start of the calendar week `weeksAgo` before the current one (0 = current).
 * Anchored on the DST-correct current-week start, then stepped back in whole
 * calendar weeks. Used by the 4-week reference so its buckets align to the same
 * weekday boundary the meter scores on.
 */
export const weekStartNAgo = (
  nowMs: number,
  weekStartDay: number,
  weeksAgo: number,
): number => {
  const d = new Date(weekWindowStart(nowMs, weekStartDay));
  d.setDate(d.getDate() - weeksAgo * 7);
  return d.getTime();
};

/**
 * Which 0-based calendar-week bucket `startedAtMs` falls in, counting back from
 * the current week, or -1 if it's `weeks` or more weeks ago (out of range) or
 * in the future. bucket 0 = current week, 1 = last week, …
 */
export const calendarWeeksAgo = (
  startedAtMs: number,
  nowMs: number,
  weekStartDay: number,
): number => {
  if (startedAtMs > nowMs) return -1;
  const curStart = weekWindowStart(nowMs, weekStartDay);
  if (startedAtMs >= curStart) return 0;
  // Whole weeks between the session and the current week's start. The `-1`
  // makes the first ms below curStart read as "1 week ago", and the exact
  // previous-week start (curStart - 7 calendar days) also read as 1, not 2.
  return Math.floor((curStart - startedAtMs - 1) / (7 * DAY_MS)) + 1;
};
